import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ListChecks } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { TaskDetailPanel } from "../components/tasks/TaskDetailPanel";
import { TYPE_META, PRIORITY_META, STATUS_META } from "../components/tasks/taskConstants";

function TaskRow({ task, onClick }) {
  return (
    <div
      className="flex items-center justify-between gap-2 p-2.5 rounded-md border hover:bg-muted/50 cursor-pointer"
      onClick={onClick}
      data-testid={`my-tasks-row-${task.id}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{task.title}</p>
        <div className="flex gap-1.5 mt-1">
          <Badge className={`text-[10px] border-0 ${TYPE_META[task.type]?.color}`}>{TYPE_META[task.type]?.label}</Badge>
          <Badge variant="outline" className="text-[10px]">{STATUS_META[task.status]?.label}</Badge>
        </div>
      </div>
      <Badge className={`text-[10px] border-0 flex-shrink-0 ${PRIORITY_META[task.priority]?.color}`}>{task.priority}</Badge>
    </div>
  );
}

function Section({ title, items, onOpen, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2"><CardTitle className="text-base">{title} ({items.length})</CardTitle></CardHeader>
      <CardContent className="space-y-1.5">
        {items.length === 0 && <p className="text-xs text-muted-foreground italic">Nada aquí</p>}
        {items.map((t) => <TaskRow key={t.id} task={t} onClick={() => onOpen(t.id)} />)}
      </CardContent>
    </Card>
  );
}

export default function MyTasksPage() {
  const { token } = useAuth();
  const [data, setData] = useState({ assigned_to_me: [], requested_by_me: [], waiting_for_me: [], pending_my_approval: [] });
  const [loading, setLoading] = useState(true);
  const [detailTaskId, setDetailTaskId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.getMyTasks(token);
      setData(r);
    } catch (e) {
      toast.error("No se pudieron cargar tus tareas");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Cargando...</div>;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="my-tasks-page">
      <div className="flex items-center gap-2">
        <ListChecks className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold">Mis tareas</h1>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Asignadas a mí" items={data.assigned_to_me} onOpen={setDetailTaskId} testId="my-tasks-assigned" />
        <Section title="Pedidas por mí" items={data.requested_by_me} onOpen={setDetailTaskId} testId="my-tasks-requested" />
        <Section title="Esperan mi respuesta" items={data.waiting_for_me} onOpen={setDetailTaskId} testId="my-tasks-waiting" />
        {data.pending_my_approval.length > 0 && (
          <Section title="Pendientes de mi aprobación (Director)" items={data.pending_my_approval} onOpen={setDetailTaskId} testId="my-tasks-pending-approval" />
        )}
      </div>
      <TaskDetailPanel taskId={detailTaskId} open={!!detailTaskId} onOpenChange={(o) => !o && setDetailTaskId(null)} onChanged={load} />
    </div>
  );
}
